/**
 * Legacy Event Handler
 *
 * Processes legacy Solomon SSE events (text, thinking, tool_use, tool_result, done, error, stream_end).
 * Returns a handler function and reset function for use in the streaming loop.
 */

import { useCallback, useMemo, useRef } from 'react';

import { generateMessageId, parseSSELine } from './sseHelpers';
import type { SolomonStreamEvent } from './sseHelpers';
import type { SolomonAgentMessage, StreamState } from './useSolomonAgent';
import { toolRegistry } from '../../lib/solomon/toolResultRegistry';
import type { SolomonAction } from '../../types/solomon';

export type LegacyHandlerCallbacks = {
  onStreamStateUpdate: (update: Partial<StreamState>) => void;
  onMessageComplete: (message: SolomonAgentMessage) => void;
  onStoreMessage: (content: string, thinking: string | undefined, toolUses: SolomonAgentMessage['toolUses']) => void;
  onEvent?: (event: SolomonStreamEvent) => void;
  onError?: (error: Error) => void;
  onApprovalRequired?: (action: SolomonAction) => void;
};

export type LegacyHandlerReturn = {
  handleLine: (line: string) => void;
  handleStreamDone: () => void;
  reset: () => void;
  getAssistantContent: () => string;
  wasMessageCreated: () => boolean;
};

export function useLegacyEventHandler(callbacks: LegacyHandlerCallbacks): LegacyHandlerReturn {
  const cbRef = useRef(callbacks);
  cbRef.current = callbacks;

  const assistantContentRef = useRef('');
  const thinkingContentRef = useRef('');
  const toolUsesRef = useRef<NonNullable<SolomonAgentMessage['toolUses']>>([]);
  const messageCreatedRef = useRef(false);

  const reset = useCallback(() => {
    assistantContentRef.current = '';
    thinkingContentRef.current = '';
    toolUsesRef.current = [];
    messageCreatedRef.current = false;
  }, []);

  const createAndEmitMessage = useCallback((sessionId?: string) => {
    const cb = cbRef.current;
    const message: SolomonAgentMessage = {
      id: generateMessageId(),
      role: 'assistant',
      content: assistantContentRef.current,
      thinking: thinkingContentRef.current || undefined,
      toolUses: toolUsesRef.current.length > 0 ? [...toolUsesRef.current] : undefined,
      timestamp: new Date(),
    };

    cb.onStoreMessage(
      assistantContentRef.current,
      thinkingContentRef.current || undefined,
      toolUsesRef.current.length > 0 ? [...toolUsesRef.current] : undefined
    );

    cb.onStreamStateUpdate({
      isStreaming: false,
      isThinking: false,
      currentResponse: '',
      currentThinking: '',
      activeToolUse: null,
      ...(sessionId ? { sessionId } : {}),
    });

    cb.onMessageComplete(message);
    messageCreatedRef.current = true;
    toolUsesRef.current = [];
    thinkingContentRef.current = '';
  }, []);

  const handleLine = useCallback(
    (line: string) => {
      const cb = cbRef.current;
      const event = parseSSELine(line);
      if (!event) return;

      cb.onEvent?.(event);

      // eslint-disable-next-line default-case
      switch (event.type) {
        case 'thinking':
          if (event.content) {
            thinkingContentRef.current += event.content;
            cb.onStreamStateUpdate({ currentThinking: thinkingContentRef.current });
          }
          break;

        case 'text':
          if (event.content) {
            assistantContentRef.current += event.content;
            cb.onStreamStateUpdate({
              isThinking: false,
              currentResponse: assistantContentRef.current,
            });
          }
          break;

        case 'tool_use':
          toolUsesRef.current.push({
            tool: event.tool || '',
            toolCallId: event.toolCallId || '',
            input: event.input,
          });
          cb.onStreamStateUpdate({
            isThinking: true,
            activeToolUse: {
              tool: event.tool || '',
              toolCallId: event.toolCallId || '',
              input: event.input,
            },
          });
          break;

        case 'tool_result': {
          const idx = toolUsesRef.current.findIndex((tu) => tu.toolCallId === event.toolCallId);
          if (idx >= 0) {
            toolUsesRef.current[idx].result = event.result;
            toolUsesRef.current[idx].isError = event.isError;
          }
          if (event.tool) {
            toolRegistry.handle(event.tool, event.result, {
              toolCallId: event.toolCallId,
              isError: event.isError,
              input: event.input,
            });
          }
          cb.onStreamStateUpdate({ activeToolUse: null });
          break;
        }

        case 'approval_required': {
          cb.onStreamStateUpdate({ isThinking: false, activeToolUse: null });
          const action: SolomonAction = {
            id: event.toolCallId || generateMessageId(),
            toolName: event.tool || '',
            description: event.description || `Approve ${event.tool} action`,
            params: event.input || {},
            tier: 'write',
            status: 'pending',
            approvalToken: event.approvalToken,
            expiresAt: event.expiresAt,
          };
          cb.onApprovalRequired?.(action);
          break;
        }

        case 'done':
          createAndEmitMessage(event.sessionId);
          break;

        case 'error': {
          const error = new Error(event.error || 'Unknown error');
          cb.onStreamStateUpdate({ isStreaming: false, isThinking: false, error });
          cb.onError?.(error);
          break;
        }

        case 'stream_end':
          cb.onStreamStateUpdate({ isStreaming: false });
          break;
      }
    },
    [createAndEmitMessage]
  );

  const handleStreamDone = useCallback(() => {
    const cb = cbRef.current;
    if (assistantContentRef.current && !messageCreatedRef.current) {
      createAndEmitMessage();
    }
    cb.onStreamStateUpdate({
      isStreaming: false,
      isThinking: false,
      currentResponse: '',
      currentThinking: '',
    });
  }, [createAndEmitMessage]);

  return useMemo(
    () => ({
      handleLine,
      handleStreamDone,
      reset,
      getAssistantContent: () => assistantContentRef.current,
      wasMessageCreated: () => messageCreatedRef.current,
    }),
    [handleLine, handleStreamDone, reset]
  );
}
